import api from './api';

/* ============ TYPY ============ */

export type ScheduleStatus = 'draft' | 'active' | 'archived';

export type ProjectPhase =
  | 'W REALIZACJI'
  | 'ZAKOŃCZONE W OKRESIE GWARANCJI'
  | 'ZAKOŃCZONE PO GWARANCJI'
  | 'ARCHIWALNY';

export interface ScheduleProjectSummary {
  project_id: string;
  project_name: string;
  client_name: string | null;
  project_phase: ProjectPhase;
  start_date: string | null;
  end_date: string | null;
  value: number;
  schedules_count: number;
  last_schedule_updated_at: string | null;
}

export interface ScheduleHeader {
  id: number;
  project_id: string;
  name: string;
  status: ScheduleStatus;
  state_date: string | null;
  notes: string | null;
  created_by_user_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface ScheduleListItem extends ScheduleHeader {
  tasks_count: number;
  tasks_value_sum: number;
  min_start: string | null;
  max_end: string | null;
}

export interface ScheduleTask {
  id: number;
  schedule_id: number;
  ordinal: number;
  outline_level: number;
  ssp: string;
  opis: string;
  days: number;
  start_date: string;
  end_date: string;
  predecessor: string | null;
  value: number;
  notes: string | null;
  progress_pct: number;
  progress_date: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface ScheduleDetail extends ScheduleHeader {
  project: {
    project_id: string;
    project_name: string;
    client_name: string | null;
    project_phase: ProjectPhase;
  } | null;
  tasks: ScheduleTask[];
}

/** Payload akceptowany przez PUT /schedules/:id/tasks (bulk save). */
export type ScheduleTaskInput = Partial<Omit<ScheduleTask, 'id' | 'schedule_id' | 'created_at' | 'updated_at'>> & {
  opis: string;
  days: number;
  start_date: string;
};

/* ============ SERVICE ============ */

function unwrap<T>(data: unknown): T {
  if (data && typeof data === 'object' && 'data' in (data as Record<string, unknown>)) {
    const r = data as { success?: boolean; data?: unknown };
    return (r.data as T) ?? (data as T);
  }
  return data as T;
}

export async function listSchedulesProjects(): Promise<ScheduleProjectSummary[]> {
  const r = await api.get('/schedules/projects');
  return unwrap<ScheduleProjectSummary[]>(r.data) || [];
}

export async function listSchedulesForProject(
  projectId: string
): Promise<{
  project: { project_id: string; project_name: string; client_name: string | null; project_phase: ProjectPhase };
  data: ScheduleListItem[];
}> {
  const r = await api.get(`/schedules/project/${encodeURIComponent(projectId)}`);
  return {
    project: r.data?.project,
    data: (r.data?.data as ScheduleListItem[]) || [],
  };
}

export async function createSchedule(
  projectId: string,
  payload: { name?: string; notes?: string; state_date?: string }
): Promise<ScheduleHeader> {
  const r = await api.post(`/schedules/project/${encodeURIComponent(projectId)}`, payload);
  return unwrap<ScheduleHeader>(r.data);
}

export async function getSchedule(id: number | string): Promise<ScheduleDetail> {
  const r = await api.get(`/schedules/${id}`);
  return unwrap<ScheduleDetail>(r.data);
}

export async function updateScheduleHeader(
  id: number | string,
  payload: Partial<Pick<ScheduleHeader, 'name' | 'notes' | 'state_date' | 'status'>>
): Promise<ScheduleHeader> {
  const r = await api.patch(`/schedules/${id}`, payload);
  return unwrap<ScheduleHeader>(r.data);
}

export async function deleteSchedule(id: number | string): Promise<void> {
  await api.delete(`/schedules/${id}`);
}

export async function duplicateSchedule(
  id: number | string,
  nameSuffix = ' (kopia)'
): Promise<ScheduleHeader> {
  const r = await api.post(`/schedules/${id}/duplicate`, { name_suffix: nameSuffix });
  return unwrap<ScheduleHeader>(r.data);
}

export async function replaceScheduleTasks(
  id: number | string,
  tasks: ScheduleTaskInput[]
): Promise<ScheduleTask[]> {
  const r = await api.put(`/schedules/${id}/tasks`, { tasks });
  return unwrap<ScheduleTask[]>(r.data);
}

export async function addScheduleTask(
  id: number | string,
  payload: ScheduleTaskInput
): Promise<ScheduleTask> {
  const r = await api.post(`/schedules/${id}/tasks`, payload);
  return unwrap<ScheduleTask>(r.data);
}

export async function updateScheduleTask(
  id: number | string,
  taskId: number | string,
  payload: Partial<ScheduleTaskInput>
): Promise<ScheduleTask> {
  const r = await api.patch(`/schedules/${id}/tasks/${taskId}`, payload);
  return unwrap<ScheduleTask>(r.data);
}

export async function deleteScheduleTask(
  id: number | string,
  taskId: number | string
): Promise<void> {
  await api.delete(`/schedules/${id}/tasks/${taskId}`);
}
